/**
 * MediaTypeBadge Component
 *
 * Shows a media type indicator badge for VIDEO, PDF, Carousel, or single Image content.
 *
 * Two variants:
 *   - `inline`: Colored pill badges shown below captions (PostTable, popovers)
 *   - `overlay`: Compact dark badge rendered on top of thumbnails; the parent
 *                is responsible for absolute positioning.
 */

import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { MEDIA_KIND_VISUALS } from '@/constants/mediaKinds';

export interface MediaTypeBadgeProps {
  mediaType: 'IMAGE' | 'VIDEO' | 'PDF' | 'DOCUMENT';
  mediaCount: number;
  variant: 'inline' | 'overlay';
}

export const MediaTypeBadge = ({ mediaType, mediaCount, variant }: MediaTypeBadgeProps) => {
  const { t } = useTranslation('common');

  if (variant === 'inline') {
    if (mediaType === 'VIDEO') {
      return (
        <span className="px-2 py-0.5 bg-soft-mint text-dark-teal text-xs font-medium rounded flex items-center gap-1">
          <span aria-hidden="true">{MEDIA_KIND_VISUALS.video.glyph}</span>
          {t('video', { defaultValue: 'Video' })}
        </span>
      );
    }

    if (mediaType === 'PDF' || mediaType === 'DOCUMENT') {
      return (
        <span className="px-2 py-0.5 bg-soft-coral text-bs-dark text-xs font-medium rounded flex items-center gap-1">
          <Icon name={MEDIA_KIND_VISUALS.document.icon} size="xs" ariaHidden />
          {mediaType === 'PDF' ? 'PDF' : t('document', { defaultValue: 'Doc' })}
        </span>
      );
    }

    // IMAGE — only render for carousel (more than one item)
    if (mediaType === 'IMAGE' && mediaCount > 1) {
      return (
        <span className="px-2 py-0.5 bg-peach text-bs-dark text-xs font-medium rounded flex items-center gap-1">
          <Icon name={MEDIA_KIND_VISUALS.carousel.icon} size="xs" ariaHidden />
          {mediaCount}
        </span>
      );
    }

    // Single IMAGE — render nothing
    return null;
  }

  // variant === 'overlay'
  const typeIcon =
    mediaType === 'VIDEO' ? '▶' : mediaType === 'PDF' || mediaType === 'DOCUMENT' ? '📄' : '🖼';

  const typeLabel =
    mediaType === 'VIDEO'
      ? t('video', { defaultValue: 'Video' })
      : mediaType === 'PDF'
        ? t('pdf', { defaultValue: 'PDF' })
        : mediaType === 'DOCUMENT'
          ? t('document', { defaultValue: 'Doc' })
          : mediaCount > 1
            ? String(mediaCount)
            : t('image', { defaultValue: 'Image' });

  return (
    <div className="flex items-center gap-1 px-1.5 py-0.5 bg-black/60 backdrop-blur-sm rounded text-white text-[10px] font-medium">
      <span aria-hidden="true">{typeIcon}</span>
      <span>{typeLabel}</span>
    </div>
  );
};
